/**
 * useCategoryGroups Hook
 * 
 * Fetches category groups by type (expense/income) from the API.
 * Uses cache so CategorySettings and ManageDetailsPage show data instantly when preloaded.
 */

import { useEffect, useState, useRef } from "react";
import type React from "react";
import { api } from "../../../services/api";
import { TransactionCategoryGroup } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";

const groupsCache = new Map<string, { data: TransactionCategoryGroup[]; timestamp: number }>();
const CACHE_TTL_MS = 90 * 1000; // 90 seconds - enough for Settings -> Categories -> Manage Details flow

export interface UseCategoryGroupsParams {
  workspaceId: string;
  type: "expense" | "income";
}

export interface UseCategoryGroupsResult {
  groups: TransactionCategoryGroup[];
  loading: boolean;
  setGroups: React.Dispatch<React.SetStateAction<TransactionCategoryGroup[]>>;
  refresh: () => Promise<void>;
}

/**
 * Hook for fetching category groups by type
 */
export const useCategoryGroups = ({ workspaceId, type }: UseCategoryGroupsParams): UseCategoryGroupsResult => {
  const cacheKey = `${workspaceId}:${type}`;
  const cached = workspaceId ? groupsCache.get(cacheKey) : null;
  const hasValidCache = !!(cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS);

  const [groups, setGroups] = useState<TransactionCategoryGroup[]>(hasValidCache && cached ? cached.data : []);
  const [loading, setLoading] = useState<boolean>(!hasValidCache);
  const isInitialMount = useRef<boolean>(!hasValidCache);

  const fetchGroups = async (): Promise<void> => {
    if (!workspaceId || isDemoMode()) {
      setGroups([]);
      setLoading(false);
      return;
    }

    if (!hasValidCache) setLoading(true);
    // Clear groups immediately when switching tabs to prevent showing wrong groups
    // Only skip clearing on initial mount to show full loading spinner
    if (!isInitialMount.current) {
      setGroups([]);
    }

    try {
      const data = await api.getGroups(workspaceId, type);
      if (workspaceId) {
        groupsCache.set(cacheKey, { data, timestamp: Date.now() });
      }
      setGroups(data);
    } catch (err) {
      console.error("Error fetching groups:", err);
      setGroups([]);
    } finally {
      setLoading(false);
      isInitialMount.current = false;
    }
  };

  useEffect(() => {
    const key = `${workspaceId}:${type}`;
    const entry = workspaceId ? groupsCache.get(key) : null;
    const valid = !!(entry && (Date.now() - entry.timestamp) < CACHE_TTL_MS);
    if (valid) return; // Skip fetch when cache is valid so we don't overwrite or race
    fetchGroups();
  }, [workspaceId, type]);

  // When we're loading, watch the cache so we show data as soon as preloader (or another instance) fills it
  useEffect(() => {
    if (!loading || !workspaceId) return;
    const key = `${workspaceId}:${type}`;
    const interval = setInterval(() => {
      const entry = groupsCache.get(key);
      const valid = entry && (Date.now() - entry.timestamp) < CACHE_TTL_MS;
      if (valid) {
        setGroups(entry.data);
        setLoading(false);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [workspaceId, type, loading]);

  // Keep cache in sync when groups change (e.g. after save); never write empty so we don't overwrite good cache
  useEffect(() => {
    if (workspaceId && groups.length > 0) {
      groupsCache.set(cacheKey, { data: groups, timestamp: Date.now() });
    }
  }, [workspaceId, cacheKey, groups]);

  return {
    groups,
    loading,
    setGroups,
    refresh: fetchGroups,
  };
};
